import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FaArrowLeft, FaExclamationCircle, FaCheckCircle, FaInfoCircle,
    FaSignOutAlt, FaUserShield, FaUserPlus, FaTrash, FaTimes,
    FaEnvelope, FaLock, FaEye, FaEyeSlash, FaCopy, FaEdit,
    FaSave, FaToggleOn, FaToggleOff, FaCog, FaFileAlt, FaUser,
} from 'react-icons/fa';
import { useRole } from '../../hooks/useRole';
import { useVisibilityRefresh } from '../../hooks/useVisibilityRefresh';
import ServiceHealthBar from '../../components/ServiceHealthBar';

const ROOT_ADMIN_EMAIL = 'manny@jdrivero.com';

const ROLE_STYLES = {
    super_admin:   'bg-red-50 text-red-800 border-red-200 hover:bg-red-100',
    editor:        'bg-blue-50 text-blue-800 border-blue-200 hover:bg-blue-100',
    store_manager: 'bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100',
    pm:            'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100',
    estimator:          'bg-violet-50 text-violet-800 border-violet-200 hover:bg-violet-100',
    arch_estimator:     'bg-indigo-50 text-indigo-800 border-indigo-200 hover:bg-indigo-100',
    construction_estimator: 'bg-orange-50 text-orange-800 border-orange-200 hover:bg-orange-100',
    client:        'bg-cyan-50 text-cyan-800 border-cyan-200 hover:bg-cyan-100',
    viewer:        'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100',
};

async function callUserAdmin(action, payload, session) {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const res = await fetch(`${supabaseUrl}/functions/v1/user-admin`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ action, ...payload }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
    return data;
}

// ── Create User Modal ─────────────────────────────────────────────────────────
function CreateUserModal({ session, onClose, onCreated, t, templates }) {
    const isRootAdmin = session?.user?.email?.toLowerCase() === ROOT_ADMIN_EMAIL;
    const [mode, setMode] = useState('invite');
    const [email, setEmail] = useState('');
    const [fullName, setFullName] = useState('');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [role, setRole] = useState('viewer');
    const [templateId, setTemplateId] = useState('');
    const [showPw, setShowPw] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    // Non-root super admins cannot create super_admin users — filter out the
    // super_admin template from the dropdown.
    const visibleTemplates = templates.filter(t => isRootAdmin || t.slug !== 'super_admin');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!email.trim()) { setError(t('admin.console.error_email_required')); return; }
        if (mode === 'password') {
            if (password.length < 8) { setError(t('admin.console.error_pw_min')); return; }
            if (password !== confirm) { setError(t('admin.console.error_pw_mismatch')); return; }
        }
        setSubmitting(true);
        try {
            const action = mode === 'invite' ? 'create_invite' : 'create_password';
            const base = { email: email.trim(), full_name: fullName.trim(), role, template_id: templateId || null };
            const payload = mode === 'invite' ? base : { ...base, password };
            await callUserAdmin(action, payload, session);
            onCreated();
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex fixed inset-0 z-50 justify-center items-center p-4 backdrop-blur-sm bg-black/40"
            onClick={e => e.target === e.currentTarget && onClose()}
        >
            <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 16 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 16 }}
                transition={{ duration: 0.18 }}
                className="overflow-hidden w-full max-w-md bg-white rounded-2xl border border-gray-200 shadow-2xl"
            >
                <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-b border-gray-100">
                    <h2 className="flex gap-2 items-center text-base font-bold text-gray-800">
                        <FaUserPlus className="text-primary" /> {t('admin.console.create_modal_title')}
                    </h2>
                    <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
                        <FaTimes size={14} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {/* Mode toggle */}
                    <div className="inline-flex gap-1 p-1 w-full bg-gray-100 rounded-xl">
                        <button
                            type="button"
                            onClick={() => setMode('invite')}
                            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                                mode === 'invite' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <FaEnvelope size={11} /> {t('admin.console.mode_invite')}
                        </button>
                        <button
                            type="button"
                            onClick={() => setMode('password')}
                            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                                mode === 'password' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <FaLock size={11} /> {t('admin.console.mode_password')}
                        </button>
                    </div>

                    {/* Full Name */}
                    <div>
                        <label className="block mb-1 text-xs font-semibold text-gray-600">{t('admin.console.full_name_label')}</label>
                        <div className="relative">
                            <FaUser className="absolute left-3 top-1/2 text-gray-300 -translate-y-1/2" size={12} />
                            <input
                                type="text"
                                value={fullName}
                                onChange={e => setFullName(e.target.value)}
                                placeholder={t('admin.console.full_name_placeholder')}
                                className="w-full pl-8 pr-3 py-2.5 text-sm rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-primary/50 transition shadow-sm"
                            />
                        </div>
                    </div>

                    {/* Email */}
                    <div>
                        <label className="block mb-1 text-xs font-semibold text-gray-600">{t('admin.console.email_label')}</label>
                        <input
                            type="email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            placeholder={t('admin.console.email_placeholder')}
                            className="w-full px-3 py-2.5 text-sm rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-primary/50 transition shadow-sm"
                            required
                        />
                    </div>

                    {/* Password fields (only in password mode) */}
                    {mode === 'password' && (
                        <>
                            <div>
                                <label className="block mb-1 text-xs font-semibold text-gray-600">{t('admin.console.password_label')}</label>
                                <div className="relative">
                                    <input
                                        type={showPw ? 'text' : 'password'}
                                        value={password}
                                        onChange={e => setPassword(e.target.value)}
                                        placeholder={t('admin.console.password_placeholder')}
                                        className="w-full pl-3 pr-10 py-2.5 text-sm rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-primary/50 transition shadow-sm"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPw(v => !v)}
                                        className="absolute right-3 top-1/2 text-gray-400 -translate-y-1/2 hover:text-gray-600"
                                    >
                                        {showPw ? <FaEyeSlash size={13} /> : <FaEye size={13} />}
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className="block mb-1 text-xs font-semibold text-gray-600">{t('admin.console.password_confirm_label')}</label>
                                <input
                                    type={showPw ? 'text' : 'password'}
                                    value={confirm}
                                    onChange={e => setConfirm(e.target.value)}
                                    placeholder={t('admin.console.password_confirm_placeholder')}
                                    className="w-full px-3 py-2.5 text-sm rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-primary/50 transition shadow-sm"
                                />
                            </div>
                        </>
                    )}

                    {/* Template */}
                    <div>
                        <label className="block mb-1 text-xs font-semibold text-gray-600">{t('admin.console.template_label')}</label>
                        <select
                            value={templateId}
                            onChange={e => setTemplateId(e.target.value)}
                            className="w-full text-sm rounded-xl border focus:ring-2 focus:ring-primary/50 focus:border-primary pl-3 pr-8 py-2.5 shadow-sm transition-colors cursor-pointer bg-white border-gray-200"
                        >
                            <option value="">{t('admin.console.no_template')}</option>
                            {visibleTemplates.map(t => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Fallback Role (when no template) */}
                    {!templateId && (
                        <div>
                            <label className="block mb-1 text-xs font-semibold text-gray-600">{t('admin.console.role_legacy_label')}</label>
                            <select
                                value={role}
                                onChange={e => setRole(e.target.value)}
                                className={`w-full text-sm rounded-xl border focus:ring-2 focus:ring-primary/50 focus:border-primary pl-3 pr-8 py-2.5 shadow-sm transition-colors cursor-pointer ${ROLE_STYLES[role] || ROLE_STYLES.viewer}`}
                            >
                                {isRootAdmin && (
                                    <option value="super_admin">{t('admin.console.role_super_admin')}</option>
                                )}
                                <option value="editor">{t('admin.console.role_editor')}</option>
                                <option value="store_manager">{t('admin.console.role_store_manager')}</option>
                                <option value="pm">{t('admin.console.role_pm')}</option>
                                <option value="estimator">{t('admin.console.role_estimator')}</option>
                                <option value="arch_estimator">{t('admin.console.role_arch_estimator')}</option>
                                <option value="construction_estimator">{t('admin.console.role_constr_estimator')}</option>
                                <option value="client">{t('admin.console.role_client')}</option>
                                <option value="viewer">{t('admin.console.role_viewer')}</option>
                            </select>
                        </div>
                    )}

                    {/* Invite mode info */}
                    {mode === 'invite' && (
                        <p className="text-[11px] text-gray-400 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                            {t('admin.console.invite_info')}
                        </p>
                    )}

                    {/* Error */}
                    {error && (
                        <p className="flex gap-2 items-center px-3 py-2 text-xs text-red-600 bg-red-50 rounded-lg border border-red-100">
                            <FaExclamationCircle size={11} /> {error}
                        </p>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2 pt-1">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-2.5 text-sm font-semibold rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                        >
                            {t('admin.console.cancel')}
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="flex-1 px-4 py-2.5 text-sm font-semibold rounded-xl bg-primary text-white hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {submitting ? t('admin.console.creating') : mode === 'invite' ? t('admin.console.btn_invite') : t('admin.console.btn_create')}
                        </button>
                    </div>
                </form>
            </motion.div>
        </motion.div>
    );
}

// ── Delete Confirmation (inline) ──────────────────────────────────────────────
function DeleteConfirm({ user, session, onCancel, onDeleted, showToast }) {
    const { t } = useTranslation();
    const [deleting, setDeleting] = useState(false);

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await callUserAdmin('delete_user', { user_id: user.user_id }, session);
            onDeleted();
        } catch (err) {
            showToast(err.message || t('admin.console.error_delete'), 'error');
            onCancel();
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="flex gap-2 justify-end items-center">
            <span className="mr-1 text-xs text-gray-500" dangerouslySetInnerHTML={{ __html: t('admin.console.delete_confirm_msg', { email: `<strong>${user.email}</strong>` }) }} />
            <button
                onClick={onCancel}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
            >
                {t('admin.console.cancel')}
            </button>
            <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-50"
            >
                {deleting ? t('admin.console.deleting') : t('admin.console.confirm_delete')}
            </button>
        </div>
    );
}

// ── Main component ────────────────────────────────────────────────────────────
const UserConsole = ({ session, onReturn, onLogout }) => {
    const { t } = useTranslation();
    const { isSuperAdmin, permissions, loading: roleLoading } = useRole();
    const isRootAdmin = session?.user?.email?.toLowerCase() === ROOT_ADMIN_EMAIL;
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState({ show: false, message: '', type: 'info' });
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [activeTab, setActiveTab] = useState('users');
    const [templates, setTemplates] = useState([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [editingName, setEditingName] = useState(null);
    const [savingName, setSavingName] = useState(false);

    const showToast = useCallback((message, type = 'info') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'info' }), 4000);
    }, []);

    const fetchUsers = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const cached = sessionStorage.getItem('user_console_users');
            if (cached) {
                setUsers(JSON.parse(cached));
                if (!silent) setLoading(false);
            }
            const { data, error } = await supabase.rpc('get_all_user_roles');
            if (error) throw error;
            setUsers(data || []);
            sessionStorage.setItem('user_console_users', JSON.stringify(data || []));
        } catch (error) {
            console.error('Error fetching users:', error);
            showToast(t('admin.console.error_fetch'), 'error');
        } finally {
            if (!silent) setLoading(false);
        }
    }, [showToast, t]);

    useEffect(() => {
        if (!roleLoading && permissions['admin.console.access']) fetchUsers();
        else if (!roleLoading && !permissions['admin.console.access']) setLoading(false);
    }, [permissions, roleLoading, fetchUsers]);

    const fetchTemplates = useCallback(async (silent = false) => {
        if (!silent) setTemplatesLoading(true);
        try {
            const cached = sessionStorage.getItem('user_console_templates');
            if (cached) {
                setTemplates(JSON.parse(cached));
                if (!silent) setTemplatesLoading(false);
            }
            const { data, error } = await supabase.rpc('get_role_templates');
            if (error) throw error;
            setTemplates(data || []);
            sessionStorage.setItem('user_console_templates', JSON.stringify(data || []));
        } catch (error) {
            console.error('Error fetching templates:', error);
            showToast(t('admin.console.error_fetch_templates'), 'error');
        } finally {
            if (!silent) setTemplatesLoading(false);
        }
    }, [showToast, t]);

    useEffect(() => {
        if (!roleLoading && permissions['admin.console.access']) {
            fetchTemplates();
        }
    }, [permissions, roleLoading, fetchTemplates]);

    const markConsoleRefreshedRef = useRef(null)

    // Silent background refresh when tab becomes visible
    const { markRefreshed: markConsoleRefreshed } = useVisibilityRefresh({
        staleMs: 300_000,
        onRefresh: useCallback(() => {
            if (!permissions['admin.console.access'] || showCreateModal || pendingDelete || editingName) {
                markConsoleRefreshedRef.current?.()
                return
            }
            const fetches = []
            if (activeTab === 'users') fetches.push(fetchUsers(true))
            if (activeTab === 'templates') fetches.push(fetchTemplates(true))
            Promise.allSettled(fetches).finally(() => markConsoleRefreshedRef.current?.())
        }, [permissions, activeTab, showCreateModal, pendingDelete, editingName, fetchUsers, fetchTemplates]),
    })
    markConsoleRefreshedRef.current = markConsoleRefreshed

    const handleRoleChange = async (targetUserId, templateId) => {
        setUsers(prev => prev.map(u => u.user_id === targetUserId ? { ...u, template_id: templateId } : u));
        try {
            const { error } = await supabase.rpc('assign_user_template', {
                p_user_id: targetUserId,
                p_template_id: templateId || null,
            });
            if (error) throw error;
            showToast(t('admin.console.success'), 'success');
            fetchUsers();
        } catch (error) {
            console.error('Failed to assign template:', error);
            showToast(t('admin.console.error_save'), 'error');
            fetchUsers();
        }
    };

    const handleCreated = () => {
        setShowCreateModal(false);
        showToast(t('admin.console.created_success'), 'success');
        fetchUsers();
    };

    const handleDeleted = () => {
        setPendingDelete(null);
        showToast(t('admin.console.deleted_success'), 'success');
        fetchUsers();
    };

    const handleSaveName = async (userId) => {
        setSavingName(true);
        try {
            await callUserAdmin('update_name', { user_id: userId, full_name: editingName.value }, session);
            showToast(t('admin.console.name_updated'), 'success');
            setEditingName(null);
            fetchUsers();
        } catch (err) {
            showToast(err.message || t('admin.console.error_save_name'), 'error');
        } finally {
            setSavingName(false);
        }
    };

    if (roleLoading) {
        return (
            <div className="flex justify-center items-center p-8 min-h-screen bg-cloud">
                <div className="mx-auto w-12 h-12 rounded-full border-b-2 animate-spin border-primary" />
            </div>
        );
    }

    if (!permissions['admin.console.access']) {
        return (
            <div className="flex flex-col justify-center items-center p-8 space-y-4 min-h-screen text-center bg-cloud">
                <FaExclamationCircle className="text-5xl text-red-500" />
                <h2 className="text-2xl font-bold text-ink">{t('admin.access.denied')}</h2>
                <p className="text-ash">{t('admin.access.req_console')}</p>
                <button onClick={onReturn} className="flex gap-2 justify-center items-center px-6 py-2 font-medium rounded-lg shadow transition-all bg-primary text-ink hover:bg-primary/90">
                    <FaArrowLeft />
                    {t('common.return_dashboard')}
                </button>
            </div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="min-h-screen overflow-x-clip bg-cloud"
        >
            {/* Header */}
            <header className="sticky top-20 z-40 bg-white border-b border-gray-200 shadow-sm">
                <div className="flex gap-3 items-center px-4 py-3 mx-auto max-w-7xl">
                    <button
                        onClick={onReturn}
                        className="p-2 rounded-lg border border-gray-200 transition-all hover:bg-gray-100 text-ash shrink-0"
                        title={t('common.return')}
                    >
                        <FaArrowLeft />
                    </button>
                    <h1 className="flex-1 text-xl font-bold text-primary">
                        {t('admin.console.title')}
                    </h1>
                    <button
                        onClick={onLogout}
                        className="p-2 rounded-lg border border-gray-200 transition-all hover:bg-red-600 hover:text-white hover:border-red-600 text-ash shrink-0"
                        title={t('auth.signOut')}
                    >
                        <FaSignOutAlt />
                    </button>
                </div>
                <div className="px-4 pb-3 mx-auto max-w-7xl sm:px-6">
                    <ServiceHealthBar />
                </div>
                {/* Tab switcher */}
                <div className="flex px-4 mx-auto max-w-7xl border-t border-gray-100 overflow-x-auto [&::-webkit-scrollbar]:hidden">
                    {[
                        { id: 'users', label: t('admin.console.tab_users'), icon: FaUserShield },
                        { id: 'templates', label: t('admin.console.tab_templates'), icon: FaUserPlus },
                    ].map(({ id, label, icon: Icon }) => (
                        <button
                            key={id}
                            onClick={() => setActiveTab(id)}
                            className={`flex-1 shrink-0 flex items-center justify-center gap-1.5 px-2 sm:px-4 py-2.5 text-xs sm:text-sm font-semibold border-b-2 transition-all -mb-px ${
                                activeTab === id ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <Icon size={13} />
                            {label}
                        </button>
                    ))}
                </div>
            </header>

            <main className="px-4 py-8 mx-auto max-w-7xl sm:px-6">
                {activeTab === 'users' && (
                <div className="overflow-hidden bg-white rounded-2xl border border-gray-200 shadow-sm">
                    {/* Table header */}
                    <div className="flex justify-between items-center p-6 bg-gray-50 border-b border-gray-100">
                        <div>
                            <h2 className="text-lg font-bold text-ink">{t('admin.console.current_accounts')}</h2>
                            <p className="mt-1 text-sm text-ash">{t('admin.console.desc')}</p>
                        </div>
                        {isSuperAdmin && (
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="flex gap-2 items-center px-4 py-2 text-sm font-semibold text-white rounded-xl shadow-sm transition-colors bg-primary hover:bg-primary/90"
                        >
                            <FaUserPlus size={13} />
                            {t('admin.console.new_user_btn')}
                        </button>
                        )}
                    </div>

                    {loading ? (
                        <div className="flex flex-col gap-3 items-center p-12 text-center animate-pulse text-ash">
                            <div className="w-8 h-8 rounded-full border-4 border-gray-200 animate-spin border-t-primary" />
                            {t('admin.console.loading')}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="text-xs tracking-wider text-gray-500 uppercase border-b border-gray-200 bg-gray-50/50">
                                        <th className="px-6 py-4 font-medium">{t('admin.console.account')}</th>
                                        <th className="px-6 py-4 font-medium">{t('admin.console.access_level')}</th>
                                        <th className="px-6 py-4 font-medium text-right">{t('admin.console.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {users.length === 0 ? (
                                        <tr>
                                            <td colSpan="3" className="px-6 py-8 italic text-center text-gray-400">
                                                {t('admin.console.empty')}
                                            </td>
                                        </tr>
                                    ) : (
                                        users.map((user) => {
                                            const isRoot = user.email === ROOT_ADMIN_EMAIL;
                                            const isOwn = user.user_id === session?.user?.id;
                                            const isTargetSuperAdmin = user.role === 'super_admin';
                                            const canDelete = isSuperAdmin && !isRoot && !isOwn && (isRootAdmin || !isTargetSuperAdmin);
                                            const isConfirming = pendingDelete?.user_id === user.user_id;

                                            return (
                                                <tr key={user.user_id} className="transition-colors hover:bg-blue-50/30">
                                                    {/* Account */}
                                                    <td className="px-6 py-4">
                                                        {editingName?.userId === user.user_id ? (
                                                            <div className="flex gap-2 items-center">
                                                                <input
                                                                    autoFocus
                                                                    type="text"
                                                                    value={editingName.value}
                                                                    onChange={e => setEditingName(v => ({ ...v, value: e.target.value }))}
                                                                    onKeyDown={e => { if (e.key === 'Enter') handleSaveName(user.user_id); if (e.key === 'Escape') setEditingName(null); }}
                                                                    className="flex-1 px-2 py-1 text-sm bg-white rounded-lg border border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/30"
                                                                    placeholder={t('admin.console.edit_name_placeholder')}
                                                                />
                                                                <button
                                                                    onClick={() => handleSaveName(user.user_id)}
                                                                    disabled={savingName}
                                                                    className="px-2 py-1 text-xs font-semibold text-white rounded-lg bg-primary hover:bg-primary/90 disabled:opacity-50"
                                                                >
                                                                    {savingName ? '…' : 'OK'}
                                                                </button>
                                                                <button
                                                                    onClick={() => setEditingName(null)}
                                                                    className="px-2 py-1 text-xs font-semibold text-gray-500 rounded-lg border border-gray-200 hover:bg-gray-50"
                                                                >
                                                                    ✕
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <div className="flex gap-2 items-start group">
                                                                <div>
                                                                    {user.full_name && (
                                                                        <div className="font-semibold text-gray-900">{user.full_name}</div>
                                                                    )}
                                                                    <div className={user.full_name ? 'text-xs text-gray-500 mt-0.5' : 'font-semibold text-gray-900'}>{user.email}</div>
                                                                    <div className="text-xs text-gray-400 font-mono mt-0.5" title="User ID">{user.user_id.split('-')[0]}***</div>
                                                                </div>
                                                                {!isRoot && isSuperAdmin && (
                                                                    <button
                                                                        onClick={() => setEditingName({ userId: user.user_id, value: user.full_name || '' })}
                                                                        className="mt-0.5 p-1 rounded text-gray-300 hover:text-primary hover:bg-primary/10 opacity-0 group-hover:opacity-100 transition-all"
                                                                        title={t('admin.console.edit_name_title')}
                                                                    >
                                                                        <FaEdit size={11} />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </td>

                                                    {/* Template */}
                                                    <td className="px-6 py-4">
                                                        {isTargetSuperAdmin ? (
                                                            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-red-50 text-red-700 border border-red-200 rounded-full text-xs font-bold shadow-sm">
                                                                <FaUserShield /> {isRoot ? t('admin.console.root_super_admin') : t('admin.console.role_super_admin')}
                                                            </span>
                                                        ) : (
                                                            <select
                                                                value={user.template_id || ''}
                                                                onChange={e => handleRoleChange(user.user_id, e.target.value || null)}
                                                                className={`text-sm rounded-lg border focus:ring-2 focus:ring-primary/50 focus:border-primary pl-3 pr-8 py-2 w-56 shadow-sm transition-colors cursor-pointer ${user.template_id ? (ROLE_STYLES[user.template_slug] || ROLE_STYLES.viewer) : 'bg-gray-50 text-gray-500 border-gray-200'}`}
                                                            >
                                                                <option value="">{t('admin.console.no_template')}</option>
                                                                {templates.filter(t => isRootAdmin || t.slug !== 'super_admin').map(t => (
                                                                    <option key={t.id} value={t.id}>{t.name}</option>
                                                                ))}
                                                            </select>
                                                        )}
                                                    </td>

                                                    {/* Actions */}
                                                    <td className="px-6 py-4 text-right">
                                                        {isRoot ? (
                                                            <span className="text-xs italic text-gray-400">{t('admin.console.cannot_downgrade')}</span>
                                                        ) : isTargetSuperAdmin && !isRootAdmin ? (
                                                            <span className="text-xs italic text-gray-400">{t('admin.console.cannot_downgrade')}</span>
                                                        ) : isConfirming ? (
                                                            <DeleteConfirm
                                                                user={user}
                                                                session={session}
                                                                onCancel={() => setPendingDelete(null)}
                                                                onDeleted={handleDeleted}
                                                                showToast={showToast}
                                                            />
                                                        ) : canDelete ? (
                                                            <button
                                                                onClick={() => setPendingDelete(user)}
                                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-red-200 text-red-500 hover:bg-red-50 hover:text-red-700 transition-colors"
                                                                title="Eliminar usuario"
                                                            >
                                                                <FaTrash size={10} /> Eliminar
                                                            </button>
                                                        ) : null}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                )}

                {activeTab === 'templates' && (
                    <TemplatesPanel
                        templates={templates}
                        templatesLoading={templatesLoading}
                        onRefresh={fetchTemplates}
                        showToast={showToast}
                        t={t}
                        isRootAdmin={isRootAdmin}
                    />
                )}
            </main>

            {/* Create User Modal */}
            <AnimatePresence>
                {showCreateModal && (
                    <CreateUserModal
                        session={session}
                        onClose={() => setShowCreateModal(false)}
                        onCreated={handleCreated}
                        t={t}
                        templates={templates}
                    />
                )}
            </AnimatePresence>

            {/* Toast */}
            <AnimatePresence>
                {toast.show && (
                    <motion.div
                        initial={{ opacity: 0, y: 50, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 50, scale: 0.95 }}
                        className="fixed right-8 bottom-8 z-50"
                    >
                        <div className={`flex items-center gap-3 px-6 py-4 rounded-xl shadow-2xl border backdrop-blur-md font-medium text-sm ${
                            toast.type === 'success' ? 'bg-green-50 text-green-800 border-green-200' :
                            toast.type === 'error'   ? 'bg-red-50 text-red-800 border-red-200' :
                            'bg-blue-50 text-blue-800 border-blue-200'
                        }`}>
                            {toast.type === 'success' && <FaCheckCircle className="text-xl" />}
                            {toast.type === 'error'   && <FaExclamationCircle className="text-xl" />}
                            {toast.type === 'info'    && <FaInfoCircle className="text-xl" />}
                            <span>{toast.message}</span>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

const PERMISSION_GROUPS = [
    {
        label: 'admin.templates.group_content',
        keys: [
            { key: 'admin.blog.access', label: 'admin.templates.perm_blog' },
            { key: 'admin.products.access', label: 'admin.templates.perm_products' },
            { key: 'admin.portfolios.access', label: 'admin.templates.perm_portfolios' },
            { key: 'admin.gallery.access', label: 'admin.templates.perm_gallery' },
            { key: 'admin.storage.access', label: 'admin.templates.perm_storage' },
        ],
    },
    {
        // Section renamed to "Solicitud de Proyectos" in the UI — the
        // permission key stays admin.monday.access so existing role
        // templates in the DB keep working without a migration.
        label: 'admin.templates.group_monday',
        keys: [
            { key: 'admin.monday.access', label: 'admin.templates.perm_monday' },
        ],
    },
    {
        label: 'admin.templates.group_workflow',
        keys: [
            { key: 'admin.workflow.access', label: 'admin.templates.perm_workflow' },
            { key: 'admin.workflow.view_architecture', label: 'admin.templates.perm_wf_arch' },
            { key: 'admin.workflow.view_construction', label: 'admin.templates.perm_wf_con' },
        ],
    },
    {
        label: 'admin.templates.group_estimator',
        keys: [
            { key: 'admin.estimator.access', label: 'admin.templates.perm_estimator' },
            { key: 'admin.estimator.construction', label: 'admin.templates.perm_est_con' },
            { key: 'admin.estimator.architecture', label: 'admin.templates.perm_est_arch' },
            { key: 'admin.estimator.edit_catalog', label: 'admin.templates.perm_est_catalog' },
            { key: 'admin.estimator.config', label: 'admin.templates.perm_est_config' },
            { key: 'admin.estimator.internal_analysis', label: 'admin.templates.perm_est_internal' },
        ],
    },
    {
        label: 'admin.templates.group_admin',
        keys: [
            { key: 'admin.console.access', label: 'admin.templates.perm_console' },
            { key: 'admin.console.manage_users', label: 'admin.templates.perm_manage_users' },
        ],
    },
];

function PermissionToggle({ label, value, onChange }) {
    return (
        <label className="flex gap-2 items-center cursor-pointer select-none">
            <button
                type="button"
                onClick={() => onChange(!value)}
                className={`shrink-0 w-9 h-5 rounded-full transition-colors relative ${value ? 'bg-primary' : 'bg-gray-300'}`}
            >
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${value ? 'translate-x-4' : ''}`} />
            </button>
            <span className="text-xs text-gray-700">{label}</span>
        </label>
    );
}

function TemplatesPanel({ templates, templatesLoading, onRefresh, showToast, t, isRootAdmin }) {
    const [editingId, setEditingId] = useState(null);
    const [editName, setEditName] = useState('');
    const [editSlug, setEditSlug] = useState('');
    const [editPerms, setEditPerms] = useState({});
    const [creating, setCreating] = useState(false);
    const [newName, setNewName] = useState('');
    const [newPerms, setNewPerms] = useState({});
    const [saving, setSaving] = useState(false);
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);

    const startEdit = (tpl) => {
        setEditingId(tpl.id);
        setEditName(tpl.name);
        setEditSlug(tpl.slug);
        setEditPerms({ ...(tpl.permissions || {}) });
        setCreating(false);
    };

    const startCreate = () => {
        setCreating(true);
        setEditingId(null);
        setNewName('');
        setNewPerms({});
    };

    const startClone = (tpl) => {
        setCreating(true);
        setEditingId(null);
        setNewName(`${tpl.name} (copy)`);
        setNewPerms({ ...(tpl.permissions || {}) });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setCreating(false);
    };

    const saveTemplate = async () => {
        const name = creating ? newName : editName;
        if (!name.trim()) { showToast(t('admin.templates.name_required'), 'error'); return; }
        const slug = creating
            ? name.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '').substring(0, 50)
            : editSlug;
        if (!slug) { showToast(t('admin.templates.invalid_slug'), 'error'); return; }
        setSaving(true);
        try {
            const perms = creating ? newPerms : editPerms;
            const { error } = await supabase.rpc('upsert_role_template', {
                p_slug: slug,
                p_name: name.trim(),
                p_permissions: perms,
            });
            if (error) throw error;
            showToast(t('admin.templates.saved'), 'success');
            setEditingId(null);
            setCreating(false);
            onRefresh();
        } catch (err) {
            console.error('Error saving template:', err);
            showToast(err.message || t('admin.templates.save_error'), 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            const { error } = await supabase.rpc('delete_role_template', { p_id: id });
            if (error) throw error;
            showToast(t('admin.templates.deleted'), 'success');
            setDeleteConfirmId(null);
            onRefresh();
        } catch (err) {
            console.error('Error deleting template:', err);
            showToast(err.message || t('admin.templates.delete_error'), 'error');
        }
    };

    const renderEditor = (name, perms, setPerms, isCreate, isSystem = false) => {
        const generatedSlug = isCreate && name.trim()
            ? name.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '').substring(0, 50)
            : null
        const slugCollision = generatedSlug && templates.some(t => t.slug === generatedSlug)
        return (
        <div className="space-y-3">
            {isSystem && (
                <p className="px-2.5 py-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg">
                    {t('admin.templates.system_locked_hint')}
                </p>
            )}
            <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1">{t('admin.templates.name')}</label>
                <input
                    type="text"
                    value={name}
                    onChange={e => isCreate ? setNewName(e.target.value) : setEditName(e.target.value)}
                    placeholder={t('admin.templates.name_placeholder')}
                    readOnly={isSystem}
                    title={isSystem ? t('admin.templates.system_locked_hint') : undefined}
                    className={`w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/50 px-3 py-1.5 ${isSystem ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-white'}`}
                />
                {generatedSlug && (
                    <p className={`mt-1 text-[10px] font-mono ${slugCollision ? 'text-red-500' : 'text-gray-400'}`}>
                        slug: <span className={slugCollision ? 'text-red-500' : 'text-primary'}>{generatedSlug}</span>
                        {slugCollision && <span className="ml-1 font-sans">⚠ {t('admin.templates.slug_collision')}</span>}
                    </p>
                )}
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {PERMISSION_GROUPS.map(g => {
                    // Non-root super admins cannot grant user management permissions
                    const visibleKeys = isRootAdmin ? g.keys : g.keys.filter(k => k.key !== 'admin.console.manage_users');
                    if (visibleKeys.length === 0) return null;
                    return (
                    <div key={g.label} className="bg-white rounded-lg border border-gray-200 p-2.5 space-y-1">
                        <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{t(g.label)}</h4>
                        {visibleKeys.map(({ key, label: l }) => (
                            <PermissionToggle
                                key={key}
                                label={t(l)}
                                value={!!perms[key]}
                                onChange={v => setPerms(p => ({ ...p, [key]: v }))}
                            />
                        ))}
                    </div>
                    );
                })}
            </div>
            <div className="flex gap-2">
                <button
                    onClick={saveTemplate}
                    disabled={saving}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                    <FaSave size={11} /> {saving ? t('common.saving') : t('common.save')}
                </button>
                <button
                    onClick={cancelEdit}
                    className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                >
                    {t('common.cancel')}
                </button>
            </div>
        </div>
    );
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-base font-bold text-ink">{t('admin.templates.title')}</h2>
                    <p className="mt-0.5 text-xs text-ash">{t('admin.templates.desc')}</p>
                </div>
                {!creating && (
                    <button
                        onClick={startCreate}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors shadow-sm"
                    >
                        <FaUserPlus size={11} /> {t('admin.templates.new')}
                    </button>
                )}
            </div>

            {creating && (
                <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                    <h3 className="mb-3 text-xs font-bold text-gray-800">{t('admin.templates.new_title')}</h3>
                    {renderEditor(newName, newPerms, setNewPerms, true)}
                </div>
            )}

            {templatesLoading ? (
                <div className="flex flex-col gap-2 items-center p-8 text-center animate-pulse text-ash">
                    <div className="w-6 h-6 rounded-full border-4 border-gray-200 animate-spin border-t-primary" />
                    {t('admin.console.loading')}
                </div>
            ) : (
                <div className="overflow-hidden bg-white rounded-xl border border-gray-200 divide-y divide-gray-100 shadow-sm">
                    {templates.length === 0 ? (
                        <div className="px-4 py-6 text-sm italic text-center text-gray-400">{t('admin.templates.empty')}</div>
                    ) : (
                        templates.map((tpl) => (
                            <React.Fragment key={tpl.id}>
                                <div className="transition-colors group hover:bg-gray-50/50">
                                    <div className="flex gap-3 items-center px-4 py-3">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex gap-2 items-center">
                                                <span className="text-sm font-semibold text-gray-900 truncate">{tpl.name}</span>
                                                {tpl.is_system ? (
                                                    <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded text-[9px] font-bold uppercase">{t('admin.templates.system')}</span>
                                                ) : (
                                                    <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 bg-gray-50 text-gray-500 border border-gray-200 rounded text-[9px] font-bold uppercase">{t('admin.templates.custom')}</span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2 mt-0.5">
                                                <span className="text-[10px] text-gray-400 font-mono">{tpl.slug}</span>
                                                <span className="text-[10px] text-gray-400">·</span>
                                                <span className="text-[10px] text-gray-500">{tpl.user_count || 0} {t('admin.templates.col_users').toLowerCase()}</span>
                                            </div>
                                        </div>
                                        <div className="flex gap-1 items-center shrink-0">
                                            <button
                                                onClick={() => startClone(tpl)}
                                                className="p-1.5 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                                                title={t('admin.templates.clone')}
                                            >
                                                <FaCopy size={11} />
                                            </button>
                                            {tpl.slug !== 'super_admin' && (
                                                <button
                                                    onClick={() => editingId === tpl.id ? cancelEdit() : startEdit(tpl)}
                                                    className={`p-1.5 rounded-md transition-colors ${editingId === tpl.id ? 'text-primary bg-primary/10' : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'}`}
                                                    title={editingId === tpl.id ? t('common.close') : t('admin.templates.edit')}
                                                >
                                                    {editingId === tpl.id ? <FaTimes size={11} /> : <FaEdit size={11} />}
                                                </button>
                                            )}
                                            {!tpl.is_system && (
                                                <button
                                                    onClick={() => setDeleteConfirmId(tpl.id)}
                                                    className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                                    title={t('admin.templates.delete')}
                                                >
                                                    <FaTrash size={11} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    {editingId === tpl.id && (
                                        <div className="px-4 pb-4">
                                            <div className="p-3 rounded-lg border border-gray-200 bg-gray-50/70">
                                                {renderEditor(editName, editPerms, setEditPerms, false, tpl.is_system)}
                                            </div>
                                        </div>
                                    )}
                                    {deleteConfirmId === tpl.id && (
                                        <div className="px-4 pb-3">
                                            <div className="flex justify-between items-center px-3 py-2 rounded-lg border border-red-100 bg-red-50/50">
                                                <span className="pr-2 text-xs text-red-700 truncate">{t('admin.templates.delete_confirm', { name: tpl.name })}</span>
                                                <div className="flex gap-1.5 shrink-0">
                                                    <button
                                                        onClick={() => setDeleteConfirmId(null)}
                                                        className="px-2 py-1 text-[10px] font-semibold rounded-md border border-gray-200 text-gray-600 hover:bg-white transition-colors"
                                                    >
                                                        {t('common.cancel')}
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(tpl.id)}
                                                        className="px-2 py-1 text-[10px] font-semibold rounded-md bg-red-500 text-white hover:bg-red-600 transition-colors"
                                                    >
                                                        {t('common.confirm')}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </React.Fragment>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}

export default UserConsole;
